import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Mail, Phone, MessageSquare, CheckCircle, Loader2, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import AnimatedText from '../components/AnimatedText';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Appointment = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    reason: '',
    comments: '',
    date: '',
    time: ''
  });

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay() || 7;

    const days = [];
    
    for (let i = startingDay - 1; i > 0; i--) {
      const prevMonth = new Date(year, month, 0);
      days.push({ date: new Date(year, month - 1, prevMonth.getDate() - i + 1), isCurrentMonth: false });
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }

    return days;
  };

  const formatDateKey = (date) => date.toISOString().split('T')[0];
  const days = getDaysInMonth(currentMonth);
  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const response = await fetch(`${API_URL}/api/public/appointments/slots?date=${date}`);
      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(data.slots);
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateSelect = (day) => {
    if (!day.isCurrentMonth || day.date < today) return;
    const dateKey = formatDateKey(day.date);
    setSelectedDate(day.date);
    setFormData({ ...formData, date: dateKey, time: '' });
    fetchAvailableSlots(dateKey);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/public/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess(true);
        // Track analytics
        fetch(`${API_URL}/api/public/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ eventType: 'form_submit', page: 'appointment' })
        }).catch(() => {});
      } else {
        const data = await response.json();
        setError(data.detail || 'Erreur lors de la réservation');
      }
    } catch (error) {
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-900 py-20 px-4">
        <div className="max-w-xl mx-auto">
          <Card className="text-center bg-gray-800 border-gray-700">
            <CardContent className="p-12">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-inter font-bold text-white mb-4">
                Rendez-vous confirmé !
              </h2>
              <p className="text-gray-400 mb-6">
                Votre demande de rendez-vous a été enregistrée. Nous vous contacterons rapidement pour confirmer.
              </p>
              <div className="bg-gray-900 rounded-lg p-4 text-left">
                <p className="text-sm text-gray-300">
                  <strong>Date :</strong> {new Date(formData.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
                <p className="text-sm text-gray-300 mt-1">
                  <strong>Heure :</strong> {formData.time}
                </p>
                <p className="text-sm text-gray-300 mt-1">
                  <strong>Motif :</strong> {formData.reason}
                </p>
              </div>
              <Button
                onClick={() => window.location.href = '/'}
                className="mt-6 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
              >
                Retour à l&apos;accueil
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedText
            text="Prendre Rendez-vous"
            className="text-4xl md:text-5xl font-inter font-bold text-white mb-4"
          />
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Réservez un créneau pour discuter de votre projet. Nos disponibilités sont de 8h à 18h, tous les jours.
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Progress Steps */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= 1 ? 'bg-amber-500 text-white' : 'bg-gray-700 text-gray-400'
              }`}>1</div>
              <div className={`w-24 h-1 ${step >= 2 ? 'bg-amber-500' : 'bg-gray-700'}`} />
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= 2 ? 'bg-amber-500 text-white' : 'bg-gray-700 text-gray-400'
              }`}>2</div>
              <div className={`w-24 h-1 ${step >= 3 ? 'bg-amber-500' : 'bg-gray-700'}`} />
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= 3 ? 'bg-amber-500 text-white' : 'bg-gray-700 text-gray-400'
              }`}>3</div>
            </div>
          </div>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-8">
              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 mb-6 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  <span className="text-red-400">{error}</span>
                </div>
              )}

              {/* Step 1: Personal Info */}
              {step === 1 && (
                <div className="space-y-6">
                  <h3 className="text-xl font-inter font-bold text-white mb-6 flex items-center gap-2">
                    <User className="w-6 h-6 text-amber-500" />
                    Vos informations
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Prénom *</label>
                      <Input
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        required
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Nom *</label>
                      <Input
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        required
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Email *</label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Téléphone *</label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Motif du rendez-vous *</label>
                    <select
                      value={formData.reason}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      className="w-full rounded-md border border-gray-600 bg-gray-700 text-white px-3 py-2"
                      required
                    >
                      <option value="">Sélectionnez un motif</option>
                      <option value="Création de site web">Création de site web</option>
                      <option value="Refonte de site">Refonte de site</option>
                      <option value="Community Management">Community Management</option>
                      <option value="Création de contenu">Création de contenu</option>
                      <option value="Stratégie digitale">Stratégie digitale</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Commentaire (optionnel)</label>
                    <textarea
                      value={formData.comments}
                      onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                      rows={3}
                      className="w-full rounded-md border border-gray-600 bg-gray-700 text-white px-3 py-2"
                      placeholder="Décrivez brièvement votre projet..."
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={() => setStep(2)}
                      disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.reason}
                      className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
                    >
                      Continuer
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2: Date Selection */}
              {step === 2 && (
                <div className="space-y-6">
                  <h3 className="text-xl font-inter font-bold text-white mb-6 flex items-center gap-2">
                    <Calendar className="w-6 h-6 text-amber-500" />
                    Choisissez une date
                  </h3>

                  {/* Month Navigation */}
                  <div className="flex items-center justify-between mb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="font-semibold text-white">
                      {currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Calendar */}
                  <div className="grid grid-cols-7 gap-1">
                    {weekDays.map(day => (
                      <div key={day} className="text-center text-sm font-medium text-gray-400 py-2">
                        {day}
                      </div>
                    ))}
                    {days.map((day, index) => {
                      const isPast = day.date < today;
                      const isSelected = selectedDate && formatDateKey(day.date) === formatDateKey(selectedDate);
                      const isToday = formatDateKey(day.date) === formatDateKey(today);

                      return (
                        <button
                          key={index}
                          onClick={() => handleDateSelect(day)}
                          disabled={!day.isCurrentMonth || isPast}
                          className={`
                            p-3 rounded-lg text-sm font-medium transition-all
                            ${!day.isCurrentMonth || isPast 
                              ? 'text-gray-600 cursor-not-allowed' 
                              : 'text-gray-300 hover:bg-gray-700 cursor-pointer'
                            }
                            ${isSelected ? 'bg-amber-500 text-white hover:bg-amber-600' : ''}
                            ${isToday && !isSelected ? 'ring-2 ring-amber-500' : ''}
                          `}
                        >
                          {day.date.getDate()}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button variant="outline" onClick={() => setStep(1)} className="border-gray-600 text-gray-300 hover:bg-gray-700 rounded-full">
                      Retour
                    </Button>
                    <Button
                      onClick={() => setStep(3)}
                      disabled={!selectedDate}
                      className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
                    >
                      Continuer
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Time Selection */}
              {step === 3 && (
                <div className="space-y-6">
                  <h3 className="text-xl font-inter font-bold text-white mb-6 flex items-center gap-2">
                    <Clock className="w-6 h-6 text-amber-500" />
                    Choisissez un horaire
                  </h3>

                  <p className="text-gray-400">
                    Date sélectionnée : <strong className="text-white">{selectedDate?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</strong>
                  </p>

                  {loadingSlots ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                    </div>
                  ) : availableSlots.length > 0 ? (
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                      {availableSlots.map(slot => (
                        <button
                          key={slot}
                          onClick={() => setFormData({ ...formData, time: slot })}
                          className={`
                            p-3 rounded-lg text-sm font-medium transition-all border
                            ${formData.time === slot 
                              ? 'bg-amber-500 text-white border-amber-500' 
                              : 'bg-gray-700 hover:bg-gray-600 border-gray-600 text-gray-300 hover:border-amber-500'
                            }
                          `}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Aucun créneau disponible pour cette date</p>
                      <p className="text-sm">Veuillez sélectionner une autre date</p>
                    </div>
                  )}

                  {/* Summary */}
                  {formData.time && (
                    <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/30">
                      <h4 className="font-semibold text-white mb-2">Récapitulatif</h4>
                      <div className="text-sm text-gray-300 space-y-1">
                        <p><strong>Nom :</strong> {formData.firstName} {formData.lastName}</p>
                        <p><strong>Email :</strong> {formData.email}</p>
                        <p><strong>Téléphone :</strong> {formData.phone}</p>
                        <p><strong>Motif :</strong> {formData.reason}</p>
                        <p><strong>Date :</strong> {selectedDate?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                        <p><strong>Heure :</strong> {formData.time} (durée : 1h)</p>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between pt-4">
                    <Button variant="outline" onClick={() => setStep(2)} className="border-gray-600 text-gray-300 hover:bg-gray-700 rounded-full">
                      Retour
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={!formData.time || loading}
                      className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Confirmer le rendez-vous'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Appointment;
